import { format, parseISO, endOfMonth, formatISO, startOfDay, addDays, startOfMonth, endOfYear, addMonths, addYears } from 'date-fns';
import React, { createContext, useState, useRef, useEffect, useMemo } from 'react';
import { enUS, de } from 'date-fns/locale';
import { getCsrfToken } from '../../../../javascripts/helpers/csrf';

export const CalendarContext = createContext({});

function CalendarContextProvider({ children, config, blocks }) {
  const initialRender = useRef(true);
  const contentRef = useRef(null);

  const locale = config.locale === 'de-DE' ? de : enUS;

  const [results, setResults] = useState(blocks);

  const [{ isOpen, isFetching, isLastPage, isLoading, hasChanged, filterCount, hasDateRange }, setState] = useState({
    isOpen: false,
    isLastPage: false,
    isLoading: false,
    isFetching: false,
    hasChanged: false,
    hasDateRange: config.date.from && config.date.to,
    filterCount: 0
  });

  const formatInitialDateSwitch = () => {
    if (config.dateSwitch.type === 'range') {
      const dateSwitchConfig = {
        items: config.dateSwitch.items.map((item) => ({ from: parseISO(item.from), to: parseISO(item.to) }))
      };

      return {
        ...dateSwitchConfig.items[0],
        ...dateSwitchConfig
      };
    }

    return {
      ...config.dateSwitch,
      from: parseISO(config.dateSwitch.from),
      to: config.dateSwitch.to ? parseISO(config.dateSwitch.to) : endOfMonth(parseISO(config.dateSwitch.from))
    };
  };

  const formatInitialCategories = () => {
    const categoriesFilter = {};

    config.filter.forEach((filter) => {
      if (filter.type === 'categories') {
        const handles = filter.items.filter((item) => item.selected);
        categoriesFilter[filter.handle] = handles.map((handle) => handle.handle);
      }
    });

    return categoriesFilter;
  };

  const getInitialFilter = () => ({
    dateSwitch: formatInitialDateSwitch(),
    categories: formatInitialCategories(),
    productions: config.productions,
    entries: {},
    date: {
      from: config.date.from ? parseISO(config.date.from) : undefined,
      to: config.date.to ? parseISO(config.date.to) : undefined
    }
  });

  const fetchResults = async (body) => {
    try {
      const { token } = await getCsrfToken(config.csrfApiUrl);

      const response = await fetch(config.apiUrl, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': token,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        const json = await response.json();
        return json;
      }

      return null;
    } catch (err) {
      // console.error(err);

      return null;
    }
  };

  const [filter, setFilter] = useState(getInitialFilter());
  const [prevFilter, setPrevFilter] = useState(getInitialFilter());

  const updateCategoriesFilter = (key, id) => {
    const newCategories = filter.categories[key].includes(id)
      ? filter.categories[key].filter((item) => item !== id)
      : [...filter.categories[key], id];

    return setFilter((prev) => ({
      ...prev,
      categories: { ...prev.categories, [key]: newCategories }
    }));
  };

  const toggleOpen = () => {
    if (isOpen) setFilter(prevFilter);
    setState((prev) => ({ ...prev, isOpen: !prev.isOpen }));
  };

  const currentMonth = useMemo(() => new Date(config.dateSwitch.items ? config.dateSwitch.items[0].from : config.dateSwitch.from), [config]);
  const currentTo = useMemo(() => new Date(config.dateSwitch.items ? config.dateSwitch.items[0].to : config.dateSwitch.to), [config]);
  const [activeMonth, setActiveMonth] = useState(format(startOfMonth(currentMonth), 'MM/dd/yy'));
  const [fetchedTo, setFetchedTo] = useState(currentTo);

  const fetchNextMonth = async () => {
    setState((prev) => ({ ...prev, isFetching: true }));

    const from = startOfDay(addDays(fetchedTo, 1));
    let to = addMonths(from, 1);

    const body = {
      categories: filter.categories,
      productions: filter.productions,
      date: {
        from: formatISO(from),
        to: formatISO(to)
      }
    };

    let json = await fetchResults(body);

    if (json && json.blocks.length === 0) {
      // If next month returns no results, try to fetch next year
      to = addYears(from, 1);
      json = await fetchResults({ ...body, date: { ...body.date, to } });
    }

    if (json) {
      setResults((prev) => [...prev, ...json.blocks]);
      setFetchedTo(to);
    }

    setState((prev) => ({ ...prev, isFetching: false, isLastPage: json && json.blocks.length === 0 }));
  };

  const fetchPreview = async () => {
    setState((prev) => ({ ...prev, isFetching: true }));

    const from = startOfMonth(addMonths(currentMonth, 2));
    const to = endOfYear(addYears(from, 2));

    const body = {
      categories: filter.categories,
      productions: filter.productions,
      date: {
        from: formatISO(from),
        to: formatISO(to)
      }
    };

    const json = await fetchResults(body);
    setState((prev) => ({ ...prev, isFetching: false }));

    if (!json) return;
    setResults((prev) => [...prev, ...json.blocks]);
  };

  const handleReset = () => {
    setFilter((prev) => ({ ...prev, ...getInitialFilter() }));
  };

  const getDateRange = () => {
    if (filter.date.from && filter.date.to) {
      return { from: filter.date.from, to: filter.date.to };
    }

    return { from: filter.dateSwitch.from, to: filter.dateSwitch.to };
  };

  const getFilterCount = () => {
    const categoriesCount = Object.keys(filter.categories).reduce((prev, curr) => prev + filter.categories[curr].length, 0);
    const dateCount = filter.date.from && filter.date.to ? 1 : 0;
    return categoriesCount + dateCount;
  };

  const handleUpdate = async () => {
    setPrevFilter(filter);

    setState((prev) => ({
      ...prev,
      isOpen: false,
      isFetching: false,
      isLastPage: false,
      isLoading: true,
      hasChanged: false,
      hasDateRange: filter.date.from && filter.date.to,
      filterCount: getFilterCount()
    }));

    const trigger = document.querySelector(`kpn-modal-trigger[modalId="${config.contentId}"]`);
    if (trigger && trigger.focus) trigger.focus();

    const date = getDateRange();
    const body = {
      categories: filter.categories,
      productions: filter.productions,
      date: {
        from: formatISO(date.from),
        to: formatISO(date.to)
      }
    };

    const json = await fetchResults(body);
    if (!json) return;
    window.scrollTo({ top: window.scrollY + contentRef.current.getBoundingClientRect().top, behavior: 'smooth' });
    setState((prev) => ({ ...prev, isLoading: false }));
    setResults(json.blocks);
  };

  useEffect(() => {
    if (typeof prevFilter !== 'undefined' && !initialRender.current) {
      const newChanged = JSON.stringify(prevFilter) !== JSON.stringify(filter);

      if (newChanged !== hasChanged) {
        setState((prev) => ({ ...prev, hasChanged: newChanged }));
      }
    }
  }, [filter]);

  useEffect(() => {
    if ((hasChanged || isOpen) && !initialRender.current) {
      // Update modal focus elements on state change
      const ev = new CustomEvent(`modal-update-${config.contentId}`, {
        bubbles: true
      });

      document.body.style.overflow = 'visible';
      window.dispatchEvent(ev);
    }
  }, [hasChanged, isOpen]);

  useEffect(() => {
    let timeout = null;

    if (initialRender.current) {
      initialRender.current = false;
    } else {
      timeout = setTimeout(() => handleUpdate(), [500]);
    }

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [filter.dateSwitch.from, filter.dateSwitch.to]);

  useEffect(() => {
    window.addEventListener(`modal-close-${config.contentId}`, toggleOpen);
    return () => window.removeEventListener(`modal-close-${config.contentId}`, toggleOpen);
  }, [isOpen]);

  const contextValue = useMemo(
    () => ({
      config,
      locale,
      currentMonth,
      updateActiveMonth: setActiveMonth,
      fetchNextMonth,
      fetchedTo,
      activeMonth,
      filter,
      updateCategoriesFilter,
      updateFilter: setFilter,
      fetchPreview,
      handleUpdate,
      handleReset,
      isLoading,
      isLastPage,
      isFetching,
      isOpen,
      toggleOpen,
      hasChanged,
      hasDateRange,
      filterCount,
      results,
      contentRef
    }),
    [
      config,
      locale,
      currentMonth,
      setActiveMonth,
      fetchNextMonth,
      fetchedTo,
      activeMonth,
      filter,
      updateCategoriesFilter,
      setFilter,
      fetchPreview,
      handleUpdate,
      handleReset,
      isLoading,
      isLastPage,
      isFetching,
      isOpen,
      toggleOpen,
      hasChanged,
      hasDateRange,
      filterCount,
      results,
      contentRef
    ]
  );

  return <CalendarContext.Provider value={contextValue}>{children}</CalendarContext.Provider>;
}

export default CalendarContextProvider;
